import React, { useState } from 'react';
import { 
  Card, 
  Timeline, 
  Typography, 
  Row, 
  Col, 
  Button, 
  Space, 
  Tag, 
  Divider,
  Tooltip
} from 'antd';
import { 
  PlayCircleOutlined, 
  DeleteOutlined, 
  DragOutlined,
  ClockCircleOutlined,
  VideoCameraOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { Video } from './types';
import { formatDuration } from './utils';

const { Title, Text } = Typography;

interface StreamTimelineProps {
  videos: Video[];
  onVideosReorder: (reorderedVideos: Video[]) => void;
  onVideoRemove: (videoId: string) => void;
  totalDuration: number;
}

const StreamTimeline: React.FC<StreamTimelineProps> = ({
  videos,
  onVideosReorder,
  onVideoRemove,
  totalDuration
}) => {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const newVideos = [...videos];
    const draggedVideo = newVideos[draggedIndex];
    
    // Remove dragged video from original position
    newVideos.splice(draggedIndex, 1);
    
    // Insert at new position
    const insertIndex = dropIndex > draggedIndex ? dropIndex - 1 : dropIndex;
    newVideos.splice(insertIndex, 0, draggedVideo);
    
    // Recalculate sequence and timing
    const reorderedVideos = newVideos.map((video, index) => ({
      ...video,
      sequence_order: index
    }));
    
    onVideosReorder(reorderedVideos);
    setDraggedIndex(null);
  };

  const calculateStartTime = (index: number): number => {
    return videos.slice(0, index).reduce((total, video) => total + video.duration, 0);
  };

  const getProgressPercentage = (duration: number): number => {
    return (duration / totalDuration) * 100;
  };

  const getTimelineColor = (index: number): string => {
    const colors = ['blue', 'green', 'orange', 'purple', 'cyan', 'magenta', 'red', 'lime'];
    return colors[index % colors.length];
  };

  const timelineItems = videos.map((video, index) => {
    const startTime = calculateStartTime(index);
    const progressPercentage = getProgressPercentage(video.duration);
    const color = getTimelineColor(index);
    console.log("video", video);
    return {
      key: video.id,
      children: (
        <Card
          size="small"
          style={{ 
            marginBottom: '4px',
            opacity: draggedIndex === index ? 0.5 : 1,
            cursor: 'move'
          }}
          draggable
          onDragStart={(e) => handleDragStart(e, index)}
          onDragOver={handleDragOver}
          onDrop={(e) => handleDrop(e, index)}
          bodyStyle={{ padding: '8px 12px' }}
        >
          <Row gutter={[12, 4]} align="middle">
            <Col flex="none">
              <DragOutlined style={{ color: '#999', fontSize: '14px' }} />
            </Col>
            <Col flex="none">
              <Tag color={color} style={{ margin: 0 }}>#{index + 1}</Tag>
            </Col>
            <Col flex="auto" style={{ minWidth: 0 }}>
              <Text strong style={{ fontSize: '13px' }}>{video.title}</Text>
            </Col>
            <Col flex="none">
              <Space size="small" split={<span style={{ color: '#d9d9d9' }}>|</span>}>
                <Space size={4}>
                  <ClockCircleOutlined style={{ fontSize: '12px', color: '#666' }} />
                  <Text style={{ fontSize: '12px' }}>{formatDuration(video.duration)}</Text>
                </Space>
                <Space size={4}>
                  <VideoCameraOutlined style={{ fontSize: '12px', color: '#666' }} />
                  <Text style={{ fontSize: '12px' }}>Start: {formatDuration(startTime)}</Text>
                </Space>
                <Text style={{ fontSize: '12px', color: '#666' }}>
                  {progressPercentage.toFixed(1)}%
                </Text>
              </Space>
            </Col>
            <Col flex="none">
              <Space size={4}>
                {video.selected_captions && video.selected_captions.length > 0 ? (
                  video.selected_captions.map(caption => (
                    <Tooltip 
                      key={caption.id} 
                      title={
                        <div>
                          <div><strong>ID:</strong> {caption.id}</div>
                          <div><strong>URL:</strong> {caption.url}</div>
                          <div><strong>Type:</strong> {caption.caption_type?.toUpperCase() ?? 'N/A'}</div>
                        </div>
                      } 
                      placement="topLeft"
                    >
                      <Tag color="green" style={{ margin: 0, fontSize: '11px', cursor: 'pointer' }}>
                        {caption.language}
                      </Tag>
                    </Tooltip>
                  ))
                ) : video.captions && video.captions.length > 0 ? (
                  video.captions
                    .filter(cap => video.selected_caption_ids?.includes(cap.id))
                    .map(caption => (
                      <Tooltip 
                        key={caption.id} 
                        title={
                          <div>
                            <div><strong>ID:</strong> {caption.id}</div>
                            <div><strong>URL:</strong> {caption.url}</div>
                            <div><strong>Type:</strong> {caption.caption_type?.toUpperCase() ?? 'N/A'}</div>
                          </div>
                        } 
                        placement="topLeft"
                      >
                        <Tag color="green" style={{ margin: 0, fontSize: '11px', cursor: 'pointer' }}>
                          {caption.language}
                        </Tag>
                      </Tooltip>
                    ))
                ) : video.caption_url ? (
                  <Tooltip 
                    title={
                      <div>
                        <div><strong>URL:</strong> {video.caption_url}</div>
                        {video.caption_type && <div><strong>Type:</strong> {video.caption_type.toUpperCase()}</div>}
                      </div>
                    } 
                    placement="topLeft"
                  >
                    <Tag color="green" style={{ margin: 0, fontSize: '11px', cursor: 'pointer' }}>
                      CC
                    </Tag>
                  </Tooltip>
                ) : (
                  <Tag color="orange" icon={<WarningOutlined />} style={{ margin: 0, fontSize: '11px' }}>
                    No captions
                  </Tag>
                )}
              </Space>
            </Col>
            <Col flex="none">
              <Button
                type="text"
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => onVideoRemove(video.id)}
                style={{ padding: '0 4px', height: '20px' }}
              />
            </Col>
          </Row>
          <Row style={{ marginTop: '4px' }}>
            <Col span={24}>
              <Text type="secondary" style={{ fontSize: '11px', wordBreak: 'break-word' }}>
                {video.ref_id}{video.file_name ? ` • ${video.file_name}` : ''}
              </Text>
            </Col>
          </Row>
        </Card>
      ),
      color: color
    };
  });

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: '16px' }}>
        <Col>
          <Title level={5}>
            Stream Timeline ({videos.length} videos)
          </Title>
        </Col>
        <Col>
          <Space>
            <Tag color="blue">
              Total Duration: {formatDuration(totalDuration)}
            </Tag>
            <Tag color={totalDuration > 24 * 60 * 60 ? 'red' : 'green'}>
              {totalDuration > 24 * 60 * 60 ? 'Over 24h' : 'Under 24h'}
            </Tag>
            {videos.length > 0 && (
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => onVideosReorder([])}
              >
                Delete All
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      {videos.length === 0 ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <PlayCircleOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
            <div>
              <Text type="secondary">No videos in timeline</Text>
            </div>
          </div>
        </Card>
      ) : (
        <Card>
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <Timeline
              items={timelineItems}
              mode="left"
            />
          </div>
          
          <Divider />
          
          <Row justify="space-between" align="middle">
            <Col>
              <Text type="secondary">
                Drag and drop videos to reorder • Click remove to delete from stream 
              </Text>
            </Col>
            <Col>
              <Space>
                <Text strong>Total Duration:</Text>
                <Text>{formatDuration(totalDuration)}</Text>
              </Space>
            </Col>
          </Row>
        </Card>
      )}
    </div>
  );
};

export default StreamTimeline;
